import { useEffect, useState, useMemo, useCallback } from 'react';
import { hubAPI } from '@/lib/api';
import api from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import { CheckSquare, Plus, Trash2, Flame, CalendarClock, User as UserIcon, Circle, CheckCircle2, AlertTriangle, ChevronDown, Check } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { Hint, InfoHint } from '@/components/common/Hint';

const STATUS_META = {
    todo: { icon: Circle, label: 'À faire', color: 'text-slate-500', bg: 'bg-slate-100', next: 'done' },
    // in_progress est toléré en lecture (données legacy) mais affiché comme "À faire".
    // Le CRM/Hub ne connaissent plus que 2 statuts : à faire / terminée.
    in_progress: { icon: Circle, label: 'À faire', color: 'text-slate-500', bg: 'bg-slate-100', next: 'done' },
    done: { icon: CheckCircle2, label: 'Terminée', color: 'text-emerald-600', bg: 'bg-emerald-100', next: 'todo' },
};

const daysLeft = (iso) => {
    if (!iso) return null;
    return Math.ceil((new Date(iso).getTime() - Date.now()) / 86400000);
};

const importanceColor = (n) => {
    if (n >= 8) return { bg: 'bg-red-100', text: 'text-red-700' };
    if (n >= 5) return { bg: 'bg-amber-100', text: 'text-amber-700' };
    if (n >= 3) return { bg: 'bg-blue-100', text: 'text-blue-700' };
    return { bg: 'bg-slate-100', text: 'text-slate-600' };
};

export function TasksPanel({ project, theme, members, canWrite }) {
    const { user } = useAuth();
    const [tasks, setTasks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [creating, setCreating] = useState(false);
    const [editingTask, setEditingTask] = useState(null);
    const [filterStage, setFilterStage] = useState('all'); // 'all' | 'current' | stage_id

    // Pipeline effectif : projet (custom) prioritaire sur thème.
    const effectivePipeline = (project?.pipeline && project.pipeline.length ? project.pipeline : (theme?.pipeline || []));

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const r = await hubAPI.listTasks(project.id);
            setTasks(r.data || []);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Erreur');
        } finally {
            setLoading(false);
        }
    }, [project.id]);

    useEffect(() => { load(); }, [load]);

    // Groupement par statut + filtre
    const visibleTasks = useMemo(() => {
        return tasks.filter((t) => {
            if (filterStage === 'all') return true;
            if (filterStage === 'current') return t.stage_id === project.current_stage_id;
            return t.stage_id === filterStage;
        });
    }, [tasks, filterStage, project.current_stage_id]);

    const grouped = useMemo(() => {
        return {
            // Les tâches legacy `in_progress` sont regroupées avec les "à faire".
            todo: visibleTasks.filter((t) => t.status === 'todo' || t.status === 'in_progress'),
            done: visibleTasks.filter((t) => t.status === 'done'),
        };
    }, [visibleTasks]);

    // Détection d'étape "endormie" : nb tâches en retard ou J-1 sur l'étape actuelle
    const currentStageTasks = tasks.filter((t) => t.stage_id === project.current_stage_id && t.status !== 'done');
    const overdueTasks = currentStageTasks.filter((t) => t.deadline && daysLeft(t.deadline) < 0);

    const toggleStatus = async (task) => {
        const next = STATUS_META[task.status]?.next || 'done';
        try {
            const r = await hubAPI.updateTask(task.id, { status: next });
            setTasks((prev) => prev.map((t) => (t.id === task.id ? r.data : t)));
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Erreur');
        }
    };

    // Change l'étape d'une tâche en un clic (menu contextuel sur le badge d'étape).
    // La tâche disparaît/apparaît immédiatement dans le filtre courant si concerné.
    const changeStage = async (task, newStageId) => {
        if (!newStageId || newStageId === task.stage_id) return;
        try {
            const r = await hubAPI.updateTask(task.id, { stage_id: newStageId });
            setTasks((prev) => prev.map((t) => (t.id === task.id ? r.data : t)));
            const stageName = effectivePipeline.find((s) => s.id === newStageId)?.name || '';
            toast.success(`Tâche déplacée sur « ${stageName} »`);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Déplacement impossible');
        }
    };

    const deleteTask = async (task) => {
        if (!window.confirm(`Supprimer la tâche « ${task.name} » ?`)) return;
        try {
            await hubAPI.deleteTask(task.id);
            setTasks((prev) => prev.filter((t) => t.id !== task.id));
            toast.success('Tâche supprimée');
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Erreur');
        }
    };

    const currentStageName = effectivePipeline.find((s) => s.id === project.current_stage_id)?.name;

    return (
        <Card data-testid="tasks-panel">
            <CardHeader className="pb-3">
                <div className="flex items-center justify-between flex-wrap gap-2">
                    <CardTitle className="text-sm text-slate-700 flex items-center gap-2">
                        <CheckSquare className="w-4 h-4" /> Tâches
                        <Badge variant="secondary" className="text-[10px]">{visibleTasks.length}</Badge>
                        <InfoHint text="Créez des tâches attribuables avec deadline pour chaque étape. Objectif : ne pas s'endormir sur une étape et savoir précisément qui fait quoi." />
                    </CardTitle>
                    <div className="flex items-center gap-2 flex-wrap">
                        <Hint text="Filtrer les tâches par étape du pipeline. Par défaut : toutes les étapes.">
                            <select
                                value={filterStage}
                                onChange={(e) => setFilterStage(e.target.value)}
                                className="h-8 text-xs rounded-md border border-slate-200 px-2"
                                data-testid="task-stage-filter"
                            >
                                <option value="all">Toutes les étapes</option>
                                <option value="current">Étape actuelle {currentStageName ? `(${currentStageName})` : ''}</option>
                                {effectivePipeline.map((s) => (
                                    <option key={s.id} value={s.id}>{s.name}</option>
                                ))}
                            </select>
                        </Hint>
                        {canWrite && (
                            <Button size="sm" onClick={() => setCreating(true)} className="gap-1" data-testid="btn-new-task">
                                <Plus className="w-4 h-4" /> Nouvelle tâche
                            </Button>
                        )}
                    </div>
                </div>
                {overdueTasks.length > 0 && (
                    <div className="mt-2 rounded-md bg-red-50 border border-red-200 px-3 py-1.5 text-xs text-red-700 flex items-center gap-2" data-testid="tasks-overdue-alert">
                        <AlertTriangle className="w-3.5 h-3.5" />
                        {overdueTasks.length} tâche{overdueTasks.length > 1 ? 's' : ''} en retard sur l&apos;étape actuelle — cette étape est en train de s&apos;endormir.
                    </div>
                )}
            </CardHeader>
            <CardContent>
                {loading ? (
                    <div className="text-xs text-slate-400 p-2">Chargement…</div>
                ) : visibleTasks.length === 0 ? (
                    <div className="text-center py-8 text-slate-400 text-sm">
                        <CheckSquare className="w-10 h-10 mx-auto mb-2 opacity-30" />
                        Aucune tâche pour cette vue.
                        {canWrite && <div className="text-[11px] mt-1">Créez la première pour piloter cette étape.</div>}
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {['todo', 'done'].map((status) => {
                            const meta = STATUS_META[status];
                            const StatusIcon = meta.icon;
                            const list = grouped[status] || [];
                            return (
                                <div key={status} data-testid={`task-col-${status}`} className="rounded-lg bg-slate-50/60 p-2">
                                    <div className="flex items-center gap-1.5 px-1 pb-2 mb-1 border-b border-slate-200">
                                        <StatusIcon className={cn("w-4 h-4", meta.color)} />
                                        <span className="text-xs font-semibold text-slate-700">{meta.label}</span>
                                        <Badge variant="secondary" className="ml-auto text-[10px]">{list.length}</Badge>
                                    </div>
                                    <div className="space-y-1.5">
                                        {list.length === 0 && <div className="text-[10px] text-slate-400 italic text-center py-2">—</div>}
                                        {list.map((t) => (
                                            <TaskCard
                                                key={t.id}
                                                task={t}
                                                members={members}
                                                theme={theme}
                                                pipeline={effectivePipeline}
                                                onToggle={() => toggleStatus(t)}
                                                onEdit={() => setEditingTask(t)}
                                                onDelete={() => deleteTask(t)}
                                                onChangeStage={(newId) => changeStage(t, newId)}
                                                canWrite={canWrite || (t.assignee_ids || []).includes(user?.id) || t.assignee_id === user?.id}
                                            />
                                        ))}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </CardContent>

            {creating && (
                <TaskDialog
                    open={creating}
                    onOpenChange={setCreating}
                    project={project}
                    theme={theme}
                    pipeline={effectivePipeline}
                    members={members}
                    onSaved={(t) => { setTasks((prev) => [...prev, t]); setCreating(false); }}
                />
            )}
            {editingTask && (
                <TaskDialog
                    open={!!editingTask}
                    onOpenChange={(o) => { if (!o) setEditingTask(null); }}
                    project={project}
                    theme={theme}
                    pipeline={effectivePipeline}
                    members={members}
                    task={editingTask}
                    onSaved={(t) => { setTasks((prev) => prev.map((x) => (x.id === t.id ? t : x))); setEditingTask(null); }}
                />
            )}
        </Card>
    );
}

function TaskCard({ task, members, theme, pipeline, onToggle, onEdit, onDelete, onChangeStage, canWrite }) {
    // Multi-assignation : assignee_ids en priorité, fallback assignee_id (legacy).
    const assigneeIds = (task.assignee_ids && task.assignee_ids.length ? task.assignee_ids : (task.assignee_id ? [task.assignee_id] : []));
    const assignees = assigneeIds.map((aid) => members.find((m) => m.id === aid)).filter(Boolean);
    // Fallback pipeline pour rétro-compat éventuelle (project ne fournit pas)
    const stagesList = (pipeline && pipeline.length ? pipeline : (theme?.pipeline || []));
    const stage = stagesList.find((s) => s.id === task.stage_id);
    const days = daysLeft(task.deadline);
    const overdue = task.status !== 'done' && days != null && days < 0;
    const soon = task.status !== 'done' && days != null && days >= 0 && days <= 2;
    const imp = importanceColor(task.importance || 0);

    return (
        <div
            role="button"
            tabIndex={0}
            className={cn(
                "group rounded-md border bg-white p-2 hover:shadow-sm hover:border-indigo-300 transition-all cursor-pointer",
                overdue ? "border-red-300" : "border-slate-200"
            )}
            onClick={onEdit}
            onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && onEdit()}
            data-testid={`task-card-${task.id}`}
        >
            <div className="flex items-start gap-2">
                <Hint text="Cliquez pour basculer entre À faire et Terminée">
                    <button
                        onClick={(e) => { e.stopPropagation(); onToggle(); }}
                        disabled={!canWrite}
                        className={cn("shrink-0 mt-0.5", canWrite ? "cursor-pointer" : "cursor-default opacity-60")}
                        data-testid={`task-toggle-${task.id}`}
                    >
                        {task.status === 'done'
                            ? <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                            : <Circle className="w-4 h-4 text-slate-400" />}
                    </button>
                </Hint>
                <div className="flex-1 min-w-0">
                    <div
                        className={cn("text-xs text-left font-medium w-full", task.status === 'done' && "line-through text-slate-400")}
                        data-testid={`task-name-${task.id}`}
                    >
                        {task.name}
                    </div>
                    <div className="flex items-center gap-1.5 flex-wrap mt-1">
                        <span className={cn("text-[9px] rounded-full px-1.5 py-0.5", imp.bg, imp.text)}>
                            <Flame className="w-2.5 h-2.5 inline mr-0.5" />{task.importance}
                        </span>
                        {task.deadline && (
                            <span className={cn(
                                "text-[9px] rounded-full px-1.5 py-0.5 flex items-center gap-0.5",
                                overdue ? "bg-red-100 text-red-700 font-semibold" : soon ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-600"
                            )}>
                                <CalendarClock className="w-2.5 h-2.5" />
                                {overdue ? `Retard ${Math.abs(days)}j` : days === 0 ? "Aujourd'hui" : `J-${days}`}
                            </span>
                        )}
                        {stage && (
                            canWrite && onChangeStage ? (
                                <Popover>
                                    <PopoverTrigger asChild>
                                        <button
                                            type="button"
                                            onClick={(e) => e.stopPropagation()}
                                            className="text-[9px] rounded-full px-1.5 py-0.5 bg-slate-100 hover:bg-slate-200 text-slate-700 inline-flex items-center gap-1 transition-colors cursor-pointer"
                                            title="Cliquer pour déplacer la tâche sur une autre étape"
                                            data-testid={`task-stage-trigger-${task.id}`}
                                        >
                                            <span className="inline-block w-1.5 h-1.5 rounded-full" style={{ backgroundColor: stage.color }} />
                                            {stage.name}
                                            <ChevronDown className="w-2.5 h-2.5" />
                                        </button>
                                    </PopoverTrigger>
                                    <PopoverContent
                                        align="start"
                                        className="w-56 p-1"
                                        onClick={(e) => e.stopPropagation()}
                                        data-testid={`task-stage-popover-${task.id}`}
                                    >
                                        <div className="px-2 py-1 text-[10px] font-semibold text-slate-500 uppercase tracking-wide">
                                            Déplacer sur…
                                        </div>
                                        <div className="max-h-64 overflow-y-auto">
                                            {stagesList.map((s) => (
                                                <button
                                                    key={s.id}
                                                    type="button"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        onChangeStage(s.id);
                                                    }}
                                                    className={cn(
                                                        'w-full flex items-center gap-2 rounded-sm px-2 py-1.5 text-xs hover:bg-slate-100 text-left transition-colors',
                                                        s.id === task.stage_id && 'bg-indigo-50 text-indigo-700 font-semibold'
                                                    )}
                                                    data-testid={`task-stage-option-${task.id}-${s.id}`}
                                                >
                                                    <span className="inline-block w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: s.color }} />
                                                    <span className="flex-1 truncate">{s.name}</span>
                                                    {s.id === task.stage_id && <Check className="w-3.5 h-3.5 text-indigo-600 shrink-0" />}
                                                </button>
                                            ))}
                                        </div>
                                    </PopoverContent>
                                </Popover>
                            ) : (
                                <span className="text-[9px] rounded-full px-1.5 py-0.5 bg-slate-100 text-slate-600 inline-flex items-center gap-1" title={`Étape : ${stage.name}`}>
                                    <span className="inline-block w-1.5 h-1.5 rounded-full" style={{ backgroundColor: stage.color }} />
                                    {stage.name}
                                </span>
                            )
                        )}
                        {assignees.length > 0 && (
                            <span
                                className="ml-auto flex items-center -space-x-1"
                                title={`Assignée à : ${assignees.map((a) => `${a.first_name} ${a.last_name}`).join(', ')}`}
                            >
                                {assignees.slice(0, 3).map((a) => (
                                    <span
                                        key={a.id}
                                        className="w-4 h-4 rounded-full bg-indigo-100 text-indigo-700 text-[8px] flex items-center justify-center font-bold border border-white"
                                    >
                                        {(a.first_name?.[0] || '?')}{(a.last_name?.[0] || '')}
                                    </span>
                                ))}
                                {assignees.length > 3 && (
                                    <span className="w-4 h-4 rounded-full bg-slate-200 text-slate-600 text-[8px] flex items-center justify-center font-bold border border-white">
                                        +{assignees.length - 3}
                                    </span>
                                )}
                            </span>
                        )}
                    </div>
                </div>
                {canWrite && (
                    <button
                        onClick={(e) => { e.stopPropagation(); onDelete(); }}
                        className="opacity-0 group-hover:opacity-100 transition-opacity text-slate-300 hover:text-red-500"
                        data-testid={`task-delete-${task.id}`}
                    >
                        <Trash2 className="w-3.5 h-3.5" />
                    </button>
                )}
            </div>
        </div>
    );
}

function TaskDialog({ open, onOpenChange, project, theme, pipeline, members, task, onSaved }) {
    const isEdit = !!task;
    // Pipeline source de vérité : celle passée en prop (déjà "effectivePipeline"),
    // avec fallback sur theme.pipeline si absent (ex: création avant chargement du projet)
    const stagesList = (pipeline && pipeline.length ? pipeline : (theme?.pipeline || []));
    const initialAssignees = task
        ? (task.assignee_ids && task.assignee_ids.length ? task.assignee_ids : (task.assignee_id ? [task.assignee_id] : []))
        : [];
    const [form, setForm] = useState({
        name: task?.name || '',
        description: task?.description || '',
        assignee_ids: initialAssignees,
        stage_id: task?.stage_id || project.current_stage_id || '',
        status: task?.status || 'todo',
        importance: task?.importance ?? 5,
        deadline: (task?.deadline || '').slice(0, 10),
        target_project_id: project.id,  // par défaut le projet courant
    });
    const [saving, setSaving] = useState(false);
    const [siblingProjects, setSiblingProjects] = useState([]);

    // Charger les autres projets du même space (uniquement en mode édition)
    useEffect(() => {
        if (!isEdit || !project?.space_id) return;
        let cancelled = false;
        (async () => {
            try {
                const { data } = await api.get(`/hub/spaces/${project.space_id}/projects`);
                const list = Array.isArray(data) ? data : (data?.items || data?.projects || []);
                if (!cancelled) setSiblingProjects(list.filter((p) => !p.is_closed));
            } catch (e) { /* silencieux */ }
        })();
        return () => { cancelled = true; };
    }, [isEdit, project?.space_id]);

    // Filtrer les membres du projet uniquement
    const projectMembers = members.filter((m) => (project.members || []).includes(m.id));
    const isMoving = isEdit && form.target_project_id && form.target_project_id !== project.id;
    const targetProject = siblingProjects.find((p) => p.id === form.target_project_id);

    const toggleAssignee = (mid) => {
        setForm((prev) => {
            const cur = prev.assignee_ids || [];
            return {
                ...prev,
                assignee_ids: cur.includes(mid) ? cur.filter((x) => x !== mid) : [...cur, mid],
            };
        });
    };

    const submit = async () => {
        if (!form.name.trim()) { toast.error('Nom obligatoire'); return; }
        if (isMoving && !window.confirm(
            `Cette tâche sera déplacée vers le projet "${targetProject?.name || ''}".\n\nElle sera placée sur la 1ère étape du nouveau projet (les pipelines diffèrent).\n\nConfirmer ?`
        )) return;
        setSaving(true);
        try {
            const payload = {
                name: form.name.trim(),
                description: form.description || null,
                assignee_ids: form.assignee_ids || [],
                stage_id: form.stage_id || null,
                importance: form.importance,
                deadline: form.deadline ? new Date(form.deadline).toISOString() : null,
            };
            // Statut modifiable en édition uniquement (à la création on part de "todo" par défaut côté back)
            if (isEdit) {
                payload.status = form.status;
            }
            if (isMoving) {
                payload.project_id = form.target_project_id;
                // Le backend ignore stage_id en cas de move ; on n'envoie pas pour éviter la confusion
                delete payload.stage_id;
            }
            const r = isEdit
                ? await hubAPI.updateTask(task.id, payload)
                : await hubAPI.createTask(project.id, payload);
            toast.success(isMoving ? `Tâche déplacée vers ${targetProject?.name}` : (isEdit ? 'Tâche mise à jour' : 'Tâche créée'));
            onSaved(r.data);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Erreur');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-md max-h-[92vh] overflow-y-auto" data-testid="task-dialog">
                <DialogHeader>
                    <DialogTitle>{isEdit ? 'Modifier la tâche' : 'Nouvelle tâche'}</DialogTitle>
                    <DialogDescription>
                        Une tâche a un ou plusieurs responsables, une deadline et une importance — pour ne rien oublier avant de passer à l&apos;étape suivante.
                    </DialogDescription>
                </DialogHeader>
                <div className="space-y-3">
                    {/* Sélecteur de projet (édition uniquement) — permet de déplacer la tâche
                        vers un autre projet du même espace de travail. */}
                    {isEdit && siblingProjects.length > 1 && (
                        <div data-testid="task-move-project-block">
                            <Label className="flex items-center gap-1.5">Projet <InfoHint text="Tu peux déplacer cette tâche vers un autre projet du même espace de travail. Elle sera placée sur la 1ère étape du nouveau projet." /></Label>
                            <select
                                value={form.target_project_id}
                                onChange={(e) => setForm({ ...form, target_project_id: e.target.value })}
                                className="w-full h-10 rounded-md border border-slate-200 px-2 text-sm"
                                data-testid="task-move-project-select"
                            >
                                {siblingProjects.map((p) => (
                                    <option key={p.id} value={p.id}>
                                        {p.name}{p.id === project.id ? ' (projet actuel)' : ''}
                                    </option>
                                ))}
                            </select>
                            {isMoving && (
                                <div className="mt-2 rounded-md border border-amber-300 bg-amber-50 px-2.5 py-1.5 text-xs text-amber-900" data-testid="task-move-warning">
                                    ⚠ La tâche sera <b>déplacée vers &laquo; {targetProject?.name} &raquo;</b> et repositionnée sur la <b>1ère étape</b> du nouveau projet (les pipelines diffèrent).
                                </div>
                            )}
                        </div>
                    )}
                    <div>
                        <Label className="flex items-center gap-1.5">Nom * <InfoHint text="Verbe d'action clair : « Envoyer le devis à X », « Valider l'étude de faisabilité »…" /></Label>
                        <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="task-name" placeholder="Ex : Contacter le partenaire..." />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <Label className="flex items-center gap-1.5">Étape rattachée <InfoHint text="Rattacher la tâche à une étape précise du pipeline. Utile pour lister les tâches par étape." /></Label>
                            <select
                                value={form.stage_id}
                                onChange={(e) => setForm({ ...form, stage_id: e.target.value })}
                                className="w-full h-10 rounded-md border border-slate-200 px-2 text-sm"
                                data-testid="task-stage"
                            >
                                <option value="">— Aucune étape —</option>
                                {stagesList.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                            </select>
                        </div>
                        <div>
                            <Label className="flex items-center gap-1.5">Deadline <InfoHint text="Date limite pour terminer la tâche. Fortement recommandée — sinon rien ne motive à avancer." /></Label>
                            <Input type="date" value={form.deadline} onChange={(e) => setForm({ ...form, deadline: e.target.value })} data-testid="task-deadline" />
                        </div>
                    </div>
                    {/* Statut modifiable en édition — permet notamment de re-passer une tâche "terminée" à "à faire" ou "en cours" */}
                    {isEdit && (
                        <div>
                            <Label className="flex items-center gap-1.5">Statut <InfoHint text="Le statut est libre dans les 2 sens : tu peux repasser une tâche « terminée » à « à faire » à tout moment." /></Label>
                            <select
                                value={form.status === 'in_progress' ? 'todo' : form.status}
                                onChange={(e) => setForm({ ...form, status: e.target.value })}
                                className="w-full h-10 rounded-md border border-slate-200 px-2 text-sm"
                                data-testid="task-status"
                            >
                                <option value="todo">🔵 À faire</option>
                                <option value="done">✅ Terminée</option>
                            </select>
                        </div>
                    )}
                    <div>
                        <Label className="flex items-center gap-1.5">
                            Assigné(s) <InfoHint text="Cochez une ou plusieurs personnes. Chaque assigné sera notifié et pourra changer le statut de la tâche. Une tâche CRM est créée automatiquement (partagée entre les co-assignés)." />
                        </Label>
                        {projectMembers.length === 0 ? (
                            <p className="text-[11px] text-amber-600 mt-1">Aucun membre dans ce projet. Ajoutez-en depuis « Modifier » sur la fiche projet.</p>
                        ) : (
                            <div className="border border-slate-200 rounded-md max-h-40 overflow-y-auto divide-y divide-slate-100" data-testid="task-assignees">
                                {projectMembers.map((m) => {
                                    const checked = form.assignee_ids.includes(m.id);
                                    return (
                                        <label
                                            key={m.id}
                                            className={cn(
                                                "flex items-center gap-2 px-2 py-1.5 text-sm cursor-pointer hover:bg-slate-50",
                                                checked && "bg-indigo-50"
                                            )}
                                            data-testid={`task-assignee-opt-${m.id}`}
                                        >
                                            <input
                                                type="checkbox"
                                                checked={checked}
                                                onChange={() => toggleAssignee(m.id)}
                                                className="h-4 w-4 rounded border-slate-300 text-indigo-600"
                                            />
                                            <span className="w-5 h-5 rounded-full bg-indigo-100 text-indigo-700 text-[9px] flex items-center justify-center font-semibold">
                                                {(m.first_name?.[0] || '?')}{(m.last_name?.[0] || '')}
                                            </span>
                                            <span className="flex-1">{m.first_name} {m.last_name}</span>
                                            {checked && <CheckCircle2 className="w-3.5 h-3.5 text-indigo-500" />}
                                        </label>
                                    );
                                })}
                            </div>
                        )}
                        <p className="text-[10px] text-slate-400 mt-1">
                            {form.assignee_ids.length === 0 ? 'Aucun assigné' : `${form.assignee_ids.length} personne${form.assignee_ids.length > 1 ? 's' : ''} sélectionnée${form.assignee_ids.length > 1 ? 's' : ''}`}
                        </p>
                    </div>
                    <div>
                        <Label>Importance : <span className="font-semibold text-indigo-600">{form.importance}/10</span></Label>
                        <input type="range" min="0" max="10" value={form.importance} onChange={(e) => setForm({ ...form, importance: parseInt(e.target.value) })} className="w-full mt-2" data-testid="task-importance" />
                    </div>
                    <div>
                        <Label>Description (optionnel)</Label>
                        <Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="task-desc" />
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)}>Annuler</Button>
                    <Button onClick={submit} disabled={saving} data-testid="task-submit">
                        {saving ? '…' : isEdit ? 'Enregistrer' : 'Créer'}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
